import {
  type OnboardingStatusResponse,
  type OnboardingStepKey,
  type TourStateResponse,
} from '@mentorivo/shared';

import { api } from '@/lib/api';

/**
 * Kurulum sihirbazı ve panel turunun istemci tarafı.
 *
 * ---------------------------------------------------------------------------
 * Adım neden ADRES SEGMENTİ (`/setup/plan`), sorgu dizesi değil
 * ---------------------------------------------------------------------------
 * Sihirbazdan iki kez dışarı çıkılıyor: bankanın 3DS sayfasına ve reklam /
 * mağaza platformunun izin ekranına. İkisinin dönüş adresi de sunucuda
 * `^/dashboard(/[\w\-/[\]]*)?$` ile süzülüyor (açık yönlendirme koruması) ve
 * `?` kabul etmiyor. `?step=plan` taşınamazdı; kullanıcı ödemeden sonra
 * sihirbazın başına ya da faturalama ayarlarına düşerdi. Segment, süzgeçten
 * olduğu gibi geçiyor ve kullanıcı tam kaldığı adıma dönüyor.
 */

/**
 * Sihirbazın EKRANLARI — sunucunun adımlarından fazlası.
 *
 * Sunucunun `plan` adımı arayüzde ÜÇ ekran (24 Eki 2026, kullanıcı: "plan
 * seçiyorsun, adım ilerlemeden kart bilgisi istiyor"): plan seçimi → fatura
 * bilgisi → ödeme. Üçü de sunucuda tek adım (`plan`) çünkü tamamlanma ölçütü
 * yine ödenmiş abonelik. Son ekran (`ready`) bir adım değil özet.
 */
export const SETUP_SCREENS = [
  'profile',
  'plan',
  'billing',
  'payment',
  'connection',
  'store',
  'team',
  'ready',
] as const;
export type SetupScreen = (typeof SETUP_SCREENS)[number];

/** Ekranın sunucudaki adımı; `ready` hiçbir adıma ait değil. */
export function serverStepOf(screen: SetupScreen): OnboardingStepKey | null {
  if (screen === 'billing' || screen === 'payment') return 'plan';
  return screen === 'ready' ? null : screen;
}

export function isSetupScreen(value: string | undefined): value is SetupScreen {
  return value !== undefined && (SETUP_SCREENS as readonly string[]).includes(value);
}

/** Alan öneki EKLENMEDEN — çağıran `withWorkspace` ile sarıyor. */
export function setupPath(screen?: SetupScreen): string {
  return screen ? `/dashboard/setup/${screen}` : '/dashboard/setup';
}

/**
 * Hesap seçimi (OAuth dönüşü) — sihirbazın çerçevesinde açılan keşif ekranı.
 * `connection` DEĞİL `accounts`: `setup/[step]` ile aynı derinlikte statik
 * bir `connection` klasörü, `/setup/connection` adresini sayfasız bir klasöre
 * düşürüp 404 verirdi.
 */
export function setupAccountsPath(platformSlug: string): string {
  return `/dashboard/setup/accounts/${platformSlug}`;
}

/**
 * Plan satın alınmadan hangi ekranlara girilebilir.
 *
 * Profil, plan, fatura ve ödeme: uçları `@AllowWithoutPlan()` taşıyor. Sonrası
 * (bağlantı, mağaza, ekip) plansız 402 alıyor — kullanıcı o ekranlara
 * adres çubuğundan da geçemiyor, plan adımına geri yollanıyor.
 */
export const SCREENS_WITHOUT_PLAN: readonly SetupScreen[] = ['profile', 'plan', 'billing', 'payment'];

/** Sunucu durumundan açılacak ekran: sıradaki adım, yoksa özet. */
export function screenFor(status: OnboardingStatusResponse): SetupScreen {
  return status.nextStep ?? 'ready';
}

/**
 * Ekran bitti mi. Fatura ekranı kayıtlı profille biter (ödeme olmadan da);
 * plan seçimi ve ödeme ekranı ödemeyle.
 */
export function screenDone(
  status: OnboardingStatusResponse,
  screen: SetupScreen,
  billingSaved: boolean,
): boolean {
  if (screen === 'ready') return status.complete;
  if (screen === 'billing') return billingSaved || stepDone(status, 'plan');
  return stepDone(status, serverStepOf(screen)!);
}

export function stepDone(status: OnboardingStatusResponse, key: OnboardingStepKey): boolean {
  return status.steps.find((step) => step.key === key)?.done ?? false;
}

export const onboardingApi = {
  status: () => api.get<OnboardingStatusResponse>('/workspaces/current/onboarding'),
  completeTour: () => api.put<TourStateResponse>('/me/tour'),
};

/**
 * Turu başka bir yerden (kullanıcı menüsü) açan olay. Tur kabukta yaşıyor,
 * menü üst çubukta; aralarına bağlam taşımak yerine tek bir pencere olayı.
 */
export const TOUR_START_EVENT = 'mentorivo:tour-start';

export function startPanelTour(): void {
  window.dispatchEvent(new Event(TOUR_START_EVENT));
}
